import { Autocomplete, TextInput } from '@cowbell/ui';
import { useEffect, useMemo, useRef, useState } from 'react';

import { listSlackChannels } from '../../api/moo-tasks.api';
import type { SlackChannel } from '../../types/moo-tasks.types';
import { useActor } from '../../../hooks/useActor';

interface Props {
  readonly label?: string;
  readonly value?: unknown;
  readonly onChange: (v: unknown) => void;
  /** The full output config (target_type, target, channel_id) — set by SchemaForm. */
  readonly form?: Record<string, unknown>;
  /** Merge several config fields at once (e.g. target + channel_id). */
  readonly setFields?: (partial: Record<string, unknown>) => void;
}

/** Map a chosen channel to the config fields we persist: a human-readable
 *  `target` (for display) plus the resolved `channel_id` (used by delivery). */
export function channelToFields(c: SlackChannel): Record<string, unknown> {
  return { target: `#${c.name}`, channel_id: c.id };
}

/**
 * Widget for a Slack output's "Target" field.
 *
 * Channel mode → a free-text input with async channel suggestions: the input is
 * usable immediately (so it never blocks on a slow/rate-limited Slack lookup);
 * suggestions load in the background, and picking one captures the resolved ID.
 * DM mode → a plain email input (target is an email).
 */
export function SlackChannelPicker(props: Props) {
  const targetType = (props.form?.target_type as string) ?? 'channel';
  const channelId = props.form?.channel_id;
  const { setFields } = props;
  const actor = useActor();
  const userEmail = actor.email ?? '';

  // Switching to DM must drop any resolved channel_id, so the stored config never
  // carries a channel target alongside a DM (email) target.
  useEffect(() => {
    if (targetType !== 'channel' && channelId) {
      setFields?.({ channel_id: undefined });
    }
  }, [targetType, channelId, setFields]);

  // Seed the DM target with the user's email the moment they switch INTO DM (and
  // only when empty) so the common "DM me" case is one click. Tracking the
  // previous target type — rather than just "currently DM and empty" — means a
  // fresh mount already in DM (the wizard unmounts inactive steps) never re-fills
  // a target the user deliberately cleared.
  const prevTargetType = useRef(targetType);
  useEffect(() => {
    const switchedIntoDm =
      prevTargetType.current !== targetType && targetType !== 'channel';
    prevTargetType.current = targetType;
    if (switchedIntoDm && !props.value && userEmail) {
      props.onChange(userEmail);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetType]);

  if (targetType !== 'channel') {
    return (
      <TextInput
        label={props.label}
        placeholder="jane@cowbell.ai"
        value={(props.value as string) ?? ''}
        onChange={(e) => props.onChange(e.currentTarget.value)}
      />
    );
  }
  return <ChannelCombobox {...props} />;
}

function ChannelCombobox({ label, value, form, setFields }: Props) {
  const currentName = (value as string) ?? (form?.target as string) ?? '';
  const [channels, setChannels] = useState<SlackChannel[]>([]);
  const [notice, setNotice] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Load suggestions in the background — typing is NEVER blocked on this. On a
  // large workspace the server fills its cache in the background and replies
  // `building: true`; we poll until the channels arrive, then they're instant.
  useEffect(() => {
    let active = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let attempts = 0;
    const MAX_ATTEMPTS = 12; // ~1 min at 5s between polls

    const load = () => {
      listSlackChannels()
        .then((resp) => {
          if (!active) return;
          const chans = resp.data.channels ?? [];
          if (chans.length) {
            setChannels(chans);
            setNotice(null);
            setLoading(false);
            return;
          }
          if (resp.data.building && attempts < MAX_ATTEMPTS) {
            attempts += 1;
            setNotice(resp.data.message ?? 'Loading channels…');
            setLoading(true);
            timer = setTimeout(load, 5000);
            return;
          }
          // Done (or gave up waiting): show whatever the server said, stop spinning.
          setNotice(resp.data.message ?? null);
          setLoading(false);
        })
        .catch(() => {
          if (!active) return;
          setNotice(
            'Channel suggestions unavailable — type the channel name or paste its ID.'
          );
          setLoading(false);
        });
    };

    load();
    return () => {
      active = false;
      if (timer) clearTimeout(timer);
    };
  }, []);

  // Suggestion labels; Autocomplete filters these client-side as the user types.
  const data = useMemo(() => channels.map((c) => `#${c.name}`), [channels]);

  // Always tell the user what to do — the field must never be a silent empty box.
  const helperText = loading
    ? notice ?? 'Loading channel suggestions…'
    : notice ??
      'Type the channel name (e.g. #moo_slack_test) or paste its Slack ID (C…). ' +
        "We'll confirm it when you save or run.";

  return (
    <Autocomplete
      label={label}
      placeholder="#channel-name or C0123ABCD"
      comboboxProps={{ withinPortal: true, zIndex: 1400 }}
      value={currentName}
      data={data}
      limit={50}
      description={helperText}
      onChange={(v: string) => {
        // Free text is always allowed (the backend resolves the name at save).
        // If it exactly matches a known channel, capture its id too so delivery
        // posts directly by id.
        const match = channels.find((c) => `#${c.name}` === v);
        setFields?.(
          match ? channelToFields(match) : { target: v, channel_id: undefined }
        );
      }}
    />
  );
}
